/** @jsx jsx */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import LinkButton from '@atlaskit/button/link';
import { css } from '@atlaskit/css';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import EditIcon from '@atlaskit/icon/core/edit';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { getRequestTypeSettingsUrl } from '@atlassian/help-center-common-util/url/url';
import messages from '../messages';

interface EditRequestTypeButtonProps {
	projectKey: string;
	requestTypeId: number;
}

const EditRequestTypeButton = (props: EditRequestTypeButtonProps) => {
	const { formatMessage } = useIntl();
	const { projectKey, requestTypeId } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClickButton = () => {
		createAnalyticsEvent({
			actionSubjectId: 'EditRequestTypeButton',
			action: 'clicked',
			actionSubject: 'button',
			analyticsType: UI_EVENT_TYPE,
		}).fire();
	};

	return (
		<Flex gap="space.100">
			<div css={ButtonStyles}>
				<LinkButton
					href={getRequestTypeSettingsUrl(projectKey, requestTypeId)}
					appearance="subtle"
					target="_blank"
					testId="edit-request-type-button"
					onClick={onClickButton}
					iconBefore={EditRequestTypeButtonEditIcon}
				>
					<Text weight="medium" color="color.text.subtle">
						{formatMessage(messages.editRequestTypeLabel)}
					</Text>
				</LinkButton>
			</div>
		</Flex>
	);
};

const EditRequestTypeButtonEditIcon = (iconProps: NewCoreIconProps) => {
	const { formatMessage } = useIntl();
	return (
		<EditIcon
			{...iconProps}
			label={formatMessage(messages.managePortalGroupsLabel)}
			spacing="none"
			color={token('color.icon', '#44546F')}
		/>
	);
};

const ButtonStyles = css({
	borderStyle: 'solid',
	borderRadius: token('radius.small', '4px'),
	borderColor: token('color.border', '#0B122824'),
	borderWidth: token('border.width', '1px'),
	height: 'fit-content',
});

export default withAnalyticsContext({ component: 'EditRequestTypeButton' })(EditRequestTypeButton);
